import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  Stack,
  Grid,
  TextField,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  InputAdornment,
  Popover,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { DateRange } from 'react-date-range';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import CloseIcon from '@mui/icons-material/Close';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { FetchContext } from 'src/context/FetchContext';
import { API_ROUTES } from 'src/services/constant';

const TEAL = '#65C8D0';
const TEAL_DARK = '#3DADB5';
const TEAL_LIGHT = '#E0F6F8';
const TEAL_MID = '#4BBBC4';

const { PRIVATE_ROUTE } = API_ROUTES;

const toMidnight = (dateStr) => {
  if (!dateStr) return new Date(new Date().setHours(0, 0, 0, 0));
  return new Date(`${String(dateStr).substring(0, 10)}T00:00:00`);
};

export default function EditEmployeeLeaveModal({ open, onClose, record, onSuccess }) {
  const { authAxios } = useContext(FetchContext);
  const [jobs, setJobs] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [taskCostItems, setTaskCostItems] = useState([]);
  const [dateRange, setDateRange] = useState([
    { startDate: new Date(), endDate: new Date(), key: 'selection' },
  ]);
  const [calendarAnchor, setCalendarAnchor] = useState(null);
  const [formData, setFormData] = useState({
    job: null,
    task: null,
    time_from: '',
    time_to: '',
    cost_item: '',
    description: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setErrors({});
      fetchJobs();
    }
  }, [open]);

  useEffect(() => {
    if (!open || !record || jobs.length === 0) return;

    const matchedJob = jobs.find((j) => String(j._id) === String(record.job_id?._id ?? record.job_id));
    const tasks = matchedJob?.task ?? [];
    const matchedTask = tasks.find((t) => String(t._id) === String(record.task_id?._id ?? record.task_id));

    const startDate = toMidnight(record.date_from);
    const endDate = record.date_to ? toMidnight(record.date_to) : startDate;

    setFilteredTasks(tasks);
    setDateRange([{ startDate, endDate, key: 'selection' }]);
    setFormData({
      job: matchedJob || null,
      task: matchedTask || null,
      time_from: record.time_from || '',
      time_to: record.time_to || '',
      cost_item: record.cost_item || '',
      description: record.description || '',
    });

    if (matchedTask) {
      fetchTaskCostItems(matchedTask._id);
    }
  }, [open, record, jobs]);

  // const fetchJobs = async () => {
  //   try {
  //     const { data } = await authAxios.get(`${PRIVATE_ROUTE.GET_JOBALL}`);
  //     const filtered = (data?.data ?? []).filter(
  //       (job) => job.sub_contract === 'NON BILLABLE' && job.is_deleted === false && job.active === true
  //     );
  //     setJobs(filtered);
  //   } catch (e) {
  //     console.error('Failed to fetch jobs', e);
  //   }
  // };

    const fetchJobs = async () => {
  try {
    const { data } = await authAxios.get(`${PRIVATE_ROUTE.GET_JOBALL}`);

    const filtered = (data?.data ?? [])
      .filter(
        (job) =>
          // job.sub_contract === 'NON BILLABLE' &&
          job.is_deleted === false &&
          job.active === true
      )
      .map((job) => ({
        ...job,
        task: (job.task || []).filter(
          (task) =>
            task.active === true && // only active tasks
            task.is_completed === 0 && // incomplete tasks
            task.is_deleted !== true &&
            task.ratesheet_name === 'NON BILLABLE'

        ),
      }))
      .filter((job) => job.task.length > 0); // optional: remove jobs with no valid tasks

    setJobs(filtered);
  } catch (e) {
    console.error('Failed to fetch jobs', e);
  }
};

  const fetchTaskCostItems = async (taskId) => {
    try {
      const { data } = await authAxios.get(`${PRIVATE_ROUTE.GET_TASK_COST_ITEMS}${taskId}`);
      setTaskCostItems(data?.data ?? []);
    } catch (e) {
      console.error('Failed to fetch task cost items', e);
    }
  };

  const handleJobChange = (selected) => {
    setFormData((f) => ({ ...f, job: selected, task: null, cost_item: '' }));
    setFilteredTasks(selected ? selected.task ?? [] : []);
    setTaskCostItems([]);
    if (errors.job) setErrors((e) => ({ ...e, job: undefined }));
  };

  const handleChange = (field, value) => {
    setFormData((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const { startDate, endDate } = dateRange[0];
  const isRange = startDate && endDate && startDate.toDateString() !== endDate.toDateString();
  const dateLabel =
    startDate && endDate
      ? isRange
        ? `${format(startDate, 'MMM dd, yyyy')} – ${format(endDate, 'MMM dd, yyyy')}`
        : format(startDate, 'MMM dd, yyyy')
      : '';

  const validate = () => {
    const newErrors = {};
    if (!formData.job) newErrors.job = 'Job is required';
    if (!formData.task) newErrors.task = 'Task is required';
    if (!startDate) newErrors.date = 'Date is required';
    if (!formData.cost_item) newErrors.cost_item = 'Cost Item is required';

    if (!isRange) {
      if (formData.time_from && formData.time_to) {
        if (formData.time_from >= formData.time_to)
          newErrors.time_to = 'Time To must be after Time From';
      } else if (formData.time_from && !formData.time_to) {
        newErrors.time_to = 'Time To is required when Time From is set';
      } else if (formData.time_to && !formData.time_from) {
        newErrors.time_from = 'Time From is required when Time To is set';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      await authAxios.post(`${PRIVATE_ROUTE.EDIT_EMPLOYEE_LEAVE}${record._id}`, {
        employee_id: record.employee_id?._id ?? record.employee_id,
        date_from: format(startDate, 'yyyy-MM-dd'),
        date_to: isRange ? format(endDate, 'yyyy-MM-dd') : null,
        job_id: formData.job?._id || null,
        task_id: formData.task?._id || null,
        time_from: formData.time_from || null,
        time_to: formData.time_to || null,
        cost_item: formData.cost_item || null,
        description: formData.description || null,
      });
      onSuccess?.();
      onClose();
    } catch (e) {
      console.error('Failed to update employee leave', e);
    }
  };

  const employeeName = record?.employee_id
    ? `${record.employee_id.first_name || ''} ${record.employee_id.last_name || ''}`.trim()
    : '';

  const fieldLabelSx = { fontSize: '0.78rem', fontWeight: 600, color: '#555', mb: 0.5 };

  return (
    <>
      <Popover
        open={Boolean(calendarAnchor)}
        anchorEl={calendarAnchor}
        onClose={() => setCalendarAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { borderRadius: 2, overflow: 'hidden', boxShadow: 6 } } }}
      >
        <DateRange
          editableDateInputs
          onChange={(item) => {
            const { startDate: s, endDate: e } = item.selection;
            setDateRange([item.selection]);
            setErrors((err) => ({ ...err, date: undefined }));
            if (s && e && s.toDateString() !== e.toDateString()) {
              setFormData((f) => ({ ...f, time_from: '', time_to: '' }));
              setErrors((err) => ({ ...err, time_from: undefined, time_to: undefined }));
            }
          }}
          moveRangeOnFirstSelection={false}
          ranges={dateRange}
          rangeColors={[TEAL_DARK]}
          color={TEAL_DARK}
          locale={enUS}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, py: 1, borderTop: `1px solid ${TEAL_LIGHT}` }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => setCalendarAnchor(null)}
            sx={{ backgroundColor: TEAL_DARK, '&:hover': { backgroundColor: TEAL }, textTransform: 'none', fontWeight: 600 }}
          >
            Done
          </Button>
        </Box>
      </Popover>

      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95vw', sm: 560 },
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '90vh',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 100%)`,
              px: 3,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <EditCalendarIcon sx={{ color: '#fff', fontSize: 22 }} />
              <Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.68rem', letterSpacing: 1.2, fontWeight: 600 }}>
                  EMPLOYEE LEAVES
                </Typography>
                <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2 }}>
                  Edit Leave Entry
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={onClose} sx={{ color: '#fff' }} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Body */}
          <Box sx={{ overflowY: 'auto', flex: 1, px: 3, py: 2.5 }}>
            <Grid container spacing={2}>
              {/* Employee — frozen */}
              <Grid item xs={12}>
                <Typography sx={fieldLabelSx}>Employee</Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={employeeName}
                  disabled
                  sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#333', cursor: 'not-allowed' } }}
                />
              </Grid>

              {/* Job */}
              <Grid item xs={12}>
                <Typography sx={{ ...fieldLabelSx, color: errors.job ? '#d32f2f' : '#555' }}>
                  Job <span style={{ color: 'red' }}>*</span>
                </Typography>
                <Autocomplete
                  size="small"
                  options={jobs}
                  getOptionLabel={(option) => option.job_number ?? ''}
                  value={formData.job}
                  onChange={(_, selected) => {
                    if (selected) handleJobChange(selected);
                    else {
                      setFormData((f) => ({ ...f, job: null, task: null, cost_item: '' }));
                      setFilteredTasks([]);
                      setTaskCostItems([]);
                    }
                  }}
                  isOptionEqualToValue={(option, value) => option._id === value._id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search or select a job"
                      error={!!errors.job}
                      helperText={errors.job}
                      sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL } }}
                    />
                  )}
                />
              </Grid>

              {/* Task */}
              <Grid item xs={12}>
                <Typography sx={{ ...fieldLabelSx, color: errors.task ? '#d32f2f' : '#555' }}>
                  Task <span style={{ color: 'red' }}>*</span>
                </Typography>
                <FormControl fullWidth size="small" error={!!errors.task}>
                  <Select
                    value={formData.task?._id ?? ''}
                    onChange={(e) => {
                      const selected = filteredTasks.find((t) => t._id === e.target.value);
                      if (selected) {
                        setFormData((f) => ({ ...f, task: selected, cost_item: '' }));
                        fetchTaskCostItems(selected._id);
                      }
                      if (errors.task) setErrors((err) => ({ ...err, task: undefined }));
                    }}
                    displayEmpty
                    disabled={!formData.job}
                    sx={{ '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL } }}
                  >
                    <MenuItem value="" disabled>
                      {formData.job
                        ? (filteredTasks.length ? 'Select a task' : 'No tasks for this job')
                        : 'Select a job first'}
                    </MenuItem>
                    {filteredTasks.map((task) => (
                      <MenuItem key={task._id} value={task._id}>
                        {task.number_str}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.task && <FormHelperText>{errors.task}</FormHelperText>}
                </FormControl>
                <Typography sx={{ ...fieldLabelSx, color: '#555', mt: 1 }}>Task Scope</Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={formData.task?.task_scope_id || ''}
                  placeholder="—"
                  InputProps={{ readOnly: true }}
                  sx={{ mt: 0.5, backgroundColor: '#f5f5f5', '& .MuiOutlinedInput-root': { pointerEvents: 'none' } }}
                />
              </Grid>

              {/* Cost Item */}
              <Grid item xs={12}>
                <Typography sx={{ ...fieldLabelSx, color: errors.cost_item ? '#d32f2f' : '#555' }}>
                  Cost Item <span style={{ color: 'red' }}>*</span>
                </Typography>
                <FormControl fullWidth size="small" error={!!errors.cost_item}>
                  <Select
                    value={formData.cost_item}
                    onChange={(e) => handleChange('cost_item', e.target.value)}
                    displayEmpty
                    disabled={!formData.task}
                    sx={{ '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL } }}
                  >
                    <MenuItem value="" disabled>
                      {!formData.task ? 'Select a task first' : taskCostItems.length === 0 ? 'No cost items for this task' : 'Select cost item'}
                    </MenuItem>
                    {taskCostItems.map((item) => (
                      <MenuItem key={item} value={item}>{item}</MenuItem>
                    ))}
                  </Select>
                  {errors.cost_item && <FormHelperText>{errors.cost_item}</FormHelperText>}
                </FormControl>
              </Grid>

              {/* Date Range */}
              <Grid item xs={12} sm={6}>
                <Typography sx={{ ...fieldLabelSx, color: errors.date ? '#d32f2f' : '#555' }}>
                  Date <span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={dateLabel}
                  onClick={(e) => setCalendarAnchor(e.currentTarget)}
                  inputProps={{ readOnly: true, style: { cursor: 'pointer' } }}
                  error={!!errors.date}
                  helperText={errors.date}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CalendarMonthIcon sx={{ fontSize: 18, color: TEAL_DARK }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { cursor: 'pointer', '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL } } }}
                />
              </Grid>

              {/* Spacer */}
              <Grid item xs={false} sm={6} sx={{ display: { xs: 'none', sm: 'block' } }} />

              {/* Time From */}
              <Grid item xs={12} sm={6}>
                <Typography sx={{ ...fieldLabelSx, color: errors.time_from ? '#d32f2f' : isRange ? '#bbb' : '#555' }}>
                  Time From <span style={{ fontWeight: 400, color: '#999', fontSize: '0.72rem' }}>(optional)</span>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="time"
                  value={formData.time_from}
                  onChange={(e) => handleChange('time_from', e.target.value)}
                  disabled={isRange}
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.time_from}
                  helperText={isRange ? 'Not available for date ranges' : errors.time_from}
                  InputProps={{
                    endAdornment: formData.time_from ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => handleChange('time_from', '')} edge="end">
                          <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL } }}
                />
              </Grid>

              {/* Time To */}
              <Grid item xs={12} sm={6}>
                <Typography sx={{ ...fieldLabelSx, color: errors.time_to ? '#d32f2f' : isRange ? '#bbb' : '#555' }}>
                  Time To <span style={{ fontWeight: 400, color: '#999', fontSize: '0.72rem' }}>(optional)</span>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="time"
                  value={formData.time_to}
                  onChange={(e) => handleChange('time_to', e.target.value)}
                  disabled={isRange}
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.time_to}
                  helperText={isRange ? 'Not available for date ranges' : errors.time_to}
                  InputProps={{
                    endAdornment: formData.time_to ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => handleChange('time_to', '')} edge="end">
                          <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL } }}
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <Typography sx={fieldLabelSx}>
                  Description <span style={{ fontWeight: 400, color: '#999', fontSize: '0.72rem' }}>(optional)</span>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL } }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              px: 3,
              py: 2,
              borderTop: `1px solid ${TEAL_LIGHT}`,
              backgroundColor: '#fafafa',
              flexShrink: 0,
            }}
          >
            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{ borderColor: TEAL_MID, color: TEAL_DARK, '&:hover': { borderColor: TEAL_DARK, backgroundColor: TEAL_LIGHT } }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{ backgroundColor: TEAL_DARK, '&:hover': { backgroundColor: TEAL }, fontWeight: 700, px: 3 }}
              >
                Update
              </Button>
            </Stack>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

EditEmployeeLeaveModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  record: PropTypes.object,
  onSuccess: PropTypes.func,
};

EditEmployeeLeaveModal.defaultProps = {
  record: null,
};
